import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, CheckCircle2, XCircle, Key, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerificarEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [email, setEmail] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/api/auth/verify-email`, {
          token
        });
        setEmail(response.data.email);
        setStatus('success');
        toast.success('Email verificado com sucesso!');
      } catch (error) {
        if (error.response?.data?.detail?.includes('expirado')) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, {
        withCredentials: true
      });
      toast.success('Novo email de verificação enviado!');
    } catch (error) {
      toast.error('Faça login para reenviar o email de verificação');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Key className="w-8 h-8 text-amber-500" />
            <span className="text-2xl font-serif text-stone-800">Author's Key</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-4" />
              <p className="text-stone-600">Verificando seu email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Email verificado!
              </h1>
              <p className="text-stone-600 mb-2">
                Seu email foi verificado com sucesso.
              </p>
              {email && (
                <p className="text-stone-800 font-medium bg-stone-100 rounded-lg py-2 px-4 mb-6 inline-block">
                  {email}
                </p>
              )}
              <p className="text-stone-500 text-sm mb-6">
                Sua conta está totalmente ativa. Aproveite todos os recursos do Author's Key!
              </p>
              <Link to="/dashboard">
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900">
                  Ir para o Dashboard
                </Button>
              </Link>
            </div>
          )}

          {status === 'expired' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Link expirado
              </h1>
              <p className="text-stone-600 mb-6">
                O link de verificação expirou. Não se preocupe, você pode solicitar um novo.
              </p>
              <Button
                onClick={handleResendVerification}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 mb-4"
              >
                Enviar novo link
              </Button>
              <Link
                to="/login"
                className="text-sm text-stone-500 hover:text-stone-700"
              >
                Voltar para o login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Link inválido
              </h1>
              <p className="text-stone-600 mb-6">
                Este link de verificação é inválido ou já foi utilizado. 
                Se você ainda não verificou seu email, solicite um novo link.
              </p>
              <Button
                onClick={handleResendVerification}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 mb-4"
              >
                Solicitar novo link
              </Button>
              <Link
                to="/login"
                className="text-sm text-stone-500 hover:text-stone-700"
              >
                Voltar para o login
              </Link>
            </div>
          )}
        </div>

        <p className="text-center text-stone-500 text-sm mt-6">
          © 2025 Author's Key. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
